import { Button, Form, Select, SelectProps, Space } from "antd";
import { DefaultOptionType } from "antd/es/cascader";
import { BaseOptionType } from "antd/es/select";
import { useEffect } from "react";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import IMG from "../../../assets/images/avatar.svg";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import useError from "../../../hooks/useError";
import { ModalComponentProps } from "../../../types";
import { generateUUID } from "../../../utils/number";
import UserWithImageNameTitle from "../../common/UserWithImageNameTitle";
import CustomModal from "../../ui/CustomModal";
import { SelectDataType } from "./AddNewApprovalModal";

const { Option } = Select;

interface ApproversModalProps extends ModalComponentProps<SelectDataType> {
  handleApprovers: (val: SelectDataType) => void;
  approvers: SelectDataType[];
}

export default function ApproversModal({
  handleClose,
  data,
  handleApprovers,
  approvers,
  type,
  ...rest
}: ApproversModalProps) {
  const [form] = Form.useForm();
  const {
    isLoading: isEmployeeLoading,
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
  } = useGetEmployeesQuery({
    params: { skip: 0, limit: 0, status: "active" },
  });

  useError(isEmployeeError, employeeError, "Get employee data fail");

  const employeeDataList =
    (employeeData?.data?.employees?.map((item) => ({
      id: item?.id,
      name: item?.member?.profile?.name,
      department: item?.role?.department?.name,
      employeeId: item?.employee_id,
      img: item?.member?.profile?.pro_pic,
    })) as SelectDataType[]) || [];

  const selectDataList = [
    {
      id: "null",
      name: "Line Manager (LM)",
      department: undefined,
      employeeId: undefined,
      img: undefined,
    },
    ...employeeDataList,
  ];

  const employeeList = selectDataList?.filter(
    (item) => !approvers?.some((ap) => ap.id === item?.id)
  );

  const onFinish = (values: any) => {
    const selectedId = values?.target_id?.value;
    const newData = selectDataList?.find((item) => item?.id === selectedId);

    if (newData) {
      if (type === "ADD") {
        newData.systemId = generateUUID();
      }
      if (type === "UPDATE") {
        newData.systemId = data?.systemId;
      }
      handleApprovers(newData);
    }
  };

  useEffect(() => {
    if (data?.id) {
      const user = selectDataList.find((u) => u.id === data.id);
      if (user) {
        form.setFieldsValue({
          target_id: {
            value: user.id,
            label: (
              <UserWithImageNameTitle
                img={user?.img || IMG}
                name={user?.name}
                title={
                  user?.employeeId || user?.department
                    ? `ID - ${user?.employeeId || "n/a"}${
                        user?.department ? `, ${user?.department}` : ""
                      }`
                    : ""
                }
              />
            ),
          },
        });
      }
    }
  }, [data?.id, form]);

  return (
    <CustomModal
      footer={false}
      handleClose={handleClose}
      width={400}
      closeIcon={null}
      title={"Select Employee"}
      {...rest}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="target_id"
          className="mb-4 substitute_id"
          rules={[{ required: true, message: "Please select employee" }]}
        >
          <SelectDetails
            data={employeeList}
            placeholder="Select employee"
            loading={isEmployeeLoading}
            showSearch
          />
        </Form.Item>

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
          >
            Submit
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}

interface SelectDetailsProps
  extends SelectProps<
    { value: string | number; label: React.ReactNode },
    BaseOptionType | DefaultOptionType
  > {
  data?: SelectDataType[];
}

function SelectDetails({ data, ...rest }: SelectDetailsProps) {
  const customRender = (id: number | string) => {
    const user = data?.find((user) => user.id === id);
    if (!user) return null;

    return (
      <UserWithImageNameTitle
        img={user?.img || IMG}
        name={user?.name}
        title={
          user?.employeeId || user?.department
            ? `ID - ${user?.employeeId || "n/a"}${
                user?.department ? `, ${user?.department}` : ""
              }`
            : ""
        }
      />
    );
  };

  return (
    <Select
      style={{ width: "100%" }}
      optionLabelProp="label"
      labelInValue
      rootClassName="select-user-with-image"
      allowClear
      suffixIcon={<SelectDropdownLgIcon />}
      filterOption={(input, option: any) => {
        const idFilter = option?.label?.props?.title
          ?.split("ID - ")?.[1]
          ?.split(",")?.[0];

        return (
          option?.label?.props?.name
            ?.toLowerCase()
            .includes(input.toLowerCase()) ||
          idFilter?.toLowerCase().includes(input.toLowerCase())
        );
      }}
      {...rest}
    >
      {data?.map((user, idx) => (
        <Option
          key={user.id || idx}
          value={user.id}
          label={customRender(user.id)}
        >
          <UserWithImageNameTitle
            img={user?.img || IMG}
            name={user?.name}
            title={`ID - ${user?.employeeId || "n/a"}, ${
              user?.department || ""
            }`}
          />
        </Option>
      ))}
    </Select>
  );
}
